import { ReactElement } from 'react';

import { PractitionerView, PractitionerViewQuery } from '@dreryk/egabinet-personnel-frontend-api';
import {
   ReceptionServiceWithPractitionersSimple,
   ReceptionServiceWithPractitionersSimpleQuery,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { useCqrsSearch } from '@dreryk/command-query-react-common';
import { Slot } from '@dreryk/watermelon';
import { Direction, paginations } from '@dreryk/command-query-ws-client';
import { Message, MessageType } from '@dreryk/react-components';

import {
   HealthcareProviderLocation,
   useGetHealthcareProviderLocations,
} from '~libs.shared.healthcare-provider-location';
import { protectFeature } from '~libs.shared.system';

import { translationKeys } from '../translations';

type GroupSessionsFilterPanelLoaderChildrenRenderProps = {
   locations: HealthcareProviderLocation[];
   practitioners: PractitionerView[];
   services: ReceptionServiceWithPractitionersSimple[];
};

type GroupSessionsFilterPanelLoaderProps = {
   children: (renderProps: GroupSessionsFilterPanelLoaderChildrenRenderProps) => ReactElement;
};

export const GroupSessionsFilterPanelLoader = (props: GroupSessionsFilterPanelLoaderProps): ReactElement | null => {
   const { data: practitioners, loading: practitionersLoading, error: practitionersFetchError } = useCqrsSearch(
      () => {
         const query = new PractitionerViewQuery();
         query.jobPositions = Slot.of(['DOCTOR']);
         return query;
      },
      paginations.all<PractitionerView>().orderedBy('lastName', Direction.ASCENDING),
      { enabled: protectFeature('searchingAllPractitioners', true, false) }
   );

   const { locations, loading: locationsLoading, error: locationsFetchError } = useGetHealthcareProviderLocations();

   const {
      data: services,
      loading: servicesLoading,
      error: servicesError,
   } = useCqrsSearch<ReceptionServiceWithPractitionersSimple>(
      () => new ReceptionServiceWithPractitionersSimpleQuery(),
      paginations.all(),
      { enabled: protectFeature('searchingPrivateMedicalServices', true, false) }
   );

   if (practitionersLoading || locationsLoading || servicesLoading) {
      return null;
   }

   if (practitionersFetchError || locationsFetchError || servicesError) {
      return (
         <Message
            testId={'failedToFetchData'}
            label={translationKeys.fetchError}
            type={MessageType.NEGATIVE}
            displayIndicator
         />
      );
   }

   return props.children({ locations: locations ?? [], practitioners: practitioners, services: services });
};
